import React, { Ref, useEffect, useState } from 'react';
import 'photoswipe/dist/photoswipe.css';
import { Gallery, Item } from 'react-photoswipe-gallery';
import {
	Box,
	Button,
	Card,
	CardContent,
	Dialog,
	IconButton,
	Link,
	Stack,
	SwipeableDrawer,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { ILockerGateItem } from 'api/interfaces/lockerGate';
import { FileServiceAPI } from 'api/services/fileServiceAPI';
import {
	AlarmType,
	DictOrderStatus,
	DictPriceCalculationConfig,
	DictUserPanelDisplayPrice,
	GateType,
	OpenStatus,
	formatDate,
	formatDateTimeWithSeconds,
	formatPhoneNumber,
	getLockerDimensions,
	getOrderPayment as getOrderPaymentState,
	parseAndFormatPrice,
} from 'shared';
import { CommunicationType, formatAccessCode, hasFlag } from '@sprytki-frontend-apps/common';
import { ButtonConfig } from 'components/alerts/rowItem/RowItemButtons';
import { getItemTypeLabel } from '../../alerts.logic';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { ClosedLockIcon } from 'assets/icons/ClosedLockIcon';
import { ClosedGateIcon, OpenGateIcon, OpenLockIcon } from 'assets';
import { getIconColor, getRentStatusColor } from 'components/alerts/alerts.logic';
import { InfIcon } from 'assets/icons/InfIcon';
import { observer } from 'mobx-react-lite';
import { locationStore } from 'stores/locationStore/locationStore';
import { CameraPreviewButton } from 'components/shared/CameraPreviewButton';
import { ItemEvents } from './ItemEvents';
import { CodeKind, EditCodeDialog } from './EditCodeDialog';

interface ItemDetailsDialogProps {
	open: boolean;
	onClose: () => void;
	buttonsConfigs: ButtonConfig[];
	lockerGateItem: ILockerGateItem;
}

const rowSx = {
	display: 'flex',
	alignItems: 'baseline',
	justifyContent: 'space-between',
	gap: 2,
	px: 1.5,
	py: 1.25,
	'&:not(:first-of-type)': { borderTop: 1, borderColor: 'borders.primary' },
};

const FieldRow: React.FC<{
	label: string;
	value: React.ReactNode;
	valueColor?: string;
	labelAdornment?: React.ReactNode;
}> = ({ label, value, valueColor, labelAdornment }) => (
	<Box sx={rowSx}>
		<Typography
			variant='body2'
			color='text.secondary'
			sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
		>
			{label}:{labelAdornment}
		</Typography>
		<Typography variant='body2' fontWeight={700} color={valueColor ?? undefined} textAlign='right'>
			{value}
		</Typography>
	</Box>
);

// A code row keeps the padlock, the spaced-out digits and the edit pencil on one baseline, so the
// codes read as one block rather than three loose pieces.
const CodeRow: React.FC<{
	label: string;
	code: string;
	suffix?: string | null;
	onEdit: () => void;
	editLabel: string;
	labelAdornment?: React.ReactNode;
}> = ({ label, code, suffix, onEdit, editLabel, labelAdornment }) => {
	const theme = useTheme();

	return (
		<Box sx={{ ...rowSx, alignItems: 'center', gap: 1 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
				<Typography variant='body2' color='text.secondary' noWrap title={label} sx={{ minWidth: 0 }}>
					{label}:
				</Typography>
				{labelAdornment}
			</Box>
			<Stack direction='row' alignItems='center' spacing={1} sx={{ flexShrink: 0 }}>
				<ClosedLockIcon color={theme.palette.secondary.main} width={12} height={18} />
				<Typography variant='body2' fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
					{formatAccessCode(code, suffix)}
				</Typography>
				<IconButton onClick={onEdit} aria-label={editLabel} size='small' sx={{ p: 0.25 }}>
					<EditIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
				</IconButton>
			</Stack>
		</Box>
	);
};

const ContactItem: React.FC<{ label: string; href: string; children: React.ReactNode }> = ({
	label,
	href,
	children,
}) => (
	<Box sx={rowSx}>
		<Typography variant='body2' color='text.secondary' sx={{ flexShrink: 0 }}>
			{label}:
		</Typography>
		<Typography variant='body2' textAlign='right' sx={{ minWidth: 0, wordBreak: 'break-word' }}>
			<Link href={href} underline='hover'>
				{children}
			</Link>
		</Typography>
	</Box>
);

const ItemDetailsDialogComponent: React.FC<ItemDetailsDialogProps> = ({
	open,
	onClose,
	buttonsConfigs,
	lockerGateItem,
}) => {
	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
	const [mapImage, setMapImage] = useState<{ src: string; width: number; height: number } | null>(null);
	const [editingCode, setEditingCode] = useState<CodeKind | null>(null);

	useEffect(() => {
		let cancelled = false;
		let objectUrl: string | null = null;

		const load = async () => {
			if (!open || !lockerGateItem.mapPreviewFileId) return;
			const url = await FileServiceAPI.getImageObjectUrl(lockerGateItem.mapPreviewFileId);
			if (!url || cancelled) return;
			objectUrl = url;
			const probe = new window.Image();
			probe.onload = () => {
				if (!cancelled) setMapImage({ src: url, width: probe.naturalWidth, height: probe.naturalHeight });
			};
			probe.src = url;
		};

		load();

		return () => {
			cancelled = true;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [open, lockerGateItem.mapPreviewFileId]);

	const showPrice = Boolean(lockerGateItem.lockerDetails?.pricing);

	// Cena z cennika przychodzi juz przeliczona i rozstrzygnieta brutto/netto po stronie API,
	// a stawka z umowy surowo w groszach - wyrownujemy je do tej samej konwencji.
	const pricing = lockerGateItem.lockerDetails?.pricing;

	const formatPrice = (value: number | undefined, divideByCents: boolean) =>
		`${parseAndFormatPrice(value, 2, divideByCents, i18n.language)} ${pricing?.currency ?? ''}`;

	// Ta sama logika co w Panelu Uzytkownika: przy displaySinglePrice sama "Cena",
	// inaczej brutto/netto wedlug ustawienia wyswietlania cen lokalizacji.
	const priceLabel = locationStore.selectedLocation?.displaySinglePrice
		? t('common.singlePrice')
		: locationStore.selectedLocation?.userPanelDisplayPrice === DictUserPanelDisplayPrice.Net
			? t('common.netPrice')
			: t('common.grossPrice');

	const getRentalPriceValue = () => {
		const rental = lockerGateItem.lockerDetails?.rentalDetails;
		if (!rental) return null;

		const minorUnits =
			locationStore.selectedLocation?.dictPriceCalculationConfig === DictPriceCalculationConfig.NettoFirst
				? rental.monthlyPriceNet
				: rental.monthlyPriceGross;

		return minorUnits / 100;
	};

	// Cennik i stawka z umowy moga sie roznic (cena indywidualna) - cennikowa ma sens
	// tylko wtedy, gdy faktycznie odbiega od tego, co placi najemca.
	const getCatalogPrice = () => {
		if (!pricing) return null;

		if (pricing.privatePrice === pricing.companyPrice) return formatPrice(pricing.privatePrice, false);

		return `${parseAndFormatPrice(pricing.privatePrice, 2, false, i18n.language)} / ${formatPrice(pricing.companyPrice, false)}`;
	};

	const catalogDiffersFromRental = () => {
		const pricing = lockerGateItem.lockerDetails?.pricing;
		const rentalValue = getRentalPriceValue();
		if (!pricing || rentalValue === null) return true;

		const catalogValue = lockerGateItem.lockerDetails?.isCompany ? pricing.companyPrice : pricing.privatePrice;

		return Math.abs(catalogValue - rentalValue) >= 0.005;
	};

	const getRentalPrice = () => {
		const pricing = lockerGateItem.lockerDetails?.pricing;
		const rental = lockerGateItem.lockerDetails?.rentalDetails;
		if (!pricing || !rental) return null;

		const value =
			locationStore.selectedLocation?.dictPriceCalculationConfig === DictPriceCalculationConfig.NettoFirst
				? rental.monthlyPriceNet
				: rental.monthlyPriceGross;

		return formatPrice(value, true);
	};

	const getItemIcon = (): React.ReactNode => {
		const iconColor = getIconColor(lockerGateItem.openStatus, theme);
		const isOpen = lockerGateItem.openStatus === OpenStatus.OpenTooLong;

		if (lockerGateItem.isLocker) {
			// An unmonitored box has no real open status — showing a lock would state something we do not know.
			if (
				!lockerGateItem.hasElectricLock ||
				lockerGateItem.alarmType === AlarmType.Satel ||
				!lockerGateItem.isMonitored
			) {
				return null;
			}
			return isOpen ? <OpenLockIcon color={iconColor} /> : <ClosedLockIcon color={iconColor} />;
		}

		return isOpen ? <OpenGateIcon color={iconColor} /> : <ClosedGateIcon color={iconColor} />;
	};

	const getRentalPeriod = () => {
		const { rentedFrom, rentedTo } = lockerGateItem.lockerDetails!.rentalDetails
			? lockerGateItem.lockerDetails!.rentalDetails
			: {};

		if (!rentedFrom) return <></>;

		return (
			<>
				{formatDate(rentedFrom)} - {rentedTo ? formatDate(rentedTo) : <InfIcon width='25' height='10' />}
			</>
		);
	};

	const getItemType = () =>
		getItemTypeLabel(
			{
				isLocker: lockerGateItem.isLocker,
				gateType: lockerGateItem.gateType,
				orderItemTypeTag: lockerGateItem.lockerDetails?.orderItemTypeTag,
			},
			t
		);
	const isGate = !lockerGateItem.isLocker && lockerGateItem.gateType !== GateType.Alarm;
	const rentalDetails = lockerGateItem.lockerDetails?.rentalDetails;
	const tenantDetails = lockerGateItem.lockerDetails?.tenantDetails;
	const visibleButtons = buttonsConfigs.filter((button) => button.show);
	const showCameraPreview = Boolean(locationStore.selectedLocation?.hasCCTV);

	// Codes belong to a box: a gate never carries them, and the API leaves both null for one. An absent
	// code means there is nothing to show or edit - same rule as the Admin Panel.
	const accessCodeLength = locationStore.selectedLocation?.accessCodeLength ?? 0;
	const accessCodeSuffix = locationStore.selectedLocation?.accessCodeSuffix;
	const accessCode = lockerGateItem.isLocker ? lockerGateItem.lockerAccessCode : null;
	const emergencyCode = lockerGateItem.isLocker ? lockerGateItem.emergencyAccessCode : null;
	// A Void-communication location uses weekend codes of 1-15 digits, so the fixed digit count in the
	// label would be a lie. Same three-way choice as SubsetTable.tsx on the Admin Panel.
	const isUsingWeekendAccessCodes = hasFlag(
		locationStore.selectedLocation?.accessCommunicationType ?? 0,
		CommunicationType.Void as number
	);
	const accessCodeLabel = isUsingWeekendAccessCodes
		? t('pages.alerts.itemDetails.weekendAccessCode')
		: accessCodeLength > 0
			? t('pages.alerts.itemDetails.accessCode', { count: accessCodeLength })
			: t('pages.alerts.itemDetails.accessCodeNoLength');
	const emergencyCodeLabel = t('pages.alerts.itemDetails.emergencyAccessCode');

	const emergencyCodeInfo = (
		<Tooltip
			title={t('pages.alerts.itemDetails.emergencyAccessCodeTooltip')}
			enterTouchDelay={0}
			leaveTouchDelay={15000}
			arrow
		>
			<IconButton
				aria-label={t('pages.alerts.itemDetails.emergencyAccessCodeTooltip')}
				size='small'
				sx={{ p: 1.5, m: -1.5, flexShrink: 0, color: 'text.disabled' }}
			>
				<InfoOutlinedIcon sx={{ fontSize: 15 }} />
			</IconButton>
		</Tooltip>
	);

	const catalogPriceRows =
		showPrice && pricing ? (
			pricing.privatePrice === pricing.companyPrice ? (
				<FieldRow label={priceLabel} value={formatPrice(pricing.privatePrice, false)} />
			) : (
				<>
					<FieldRow label={t('common.privatePrice')} value={formatPrice(pricing.privatePrice, false)} />
					<FieldRow label={t('common.companyPrice')} value={formatPrice(pricing.companyPrice, false)} />
				</>
			)
		) : null;

	const actionButtons: { key: string; element: () => React.ReactNode }[] = [
		...visibleButtons.map((buttonConfig, index) => ({
			key: `${buttonConfig.label}-${index}`,
			element: () => (
				<Button variant='outlined' color='primary' onClick={buttonConfig.onClick} fullWidth size='small'>
					{buttonConfig.labelShort || buttonConfig.label}
				</Button>
			),
		})),
		...(showCameraPreview
			? [
					{
						key: 'cameraPreview',
						element: () => (
							<CameraPreviewButton
								locationId={locationStore.selectedLocation!.id}
								queryParams={
									lockerGateItem.isLocker
										? { lockerId: lockerGateItem.id }
										: { gateId: lockerGateItem.id }
								}
								fullWidth
								size='small'
							/>
						),
					},
				]
			: []),
		...(lockerGateItem.mapPreviewFileId
			? [
					{
						key: 'mapPreview',
						element: () => (
							<Gallery options={{ bgOpacity: 0.95 }}>
								<Item
									original={mapImage?.src}
									thumbnail={mapImage?.src}
									width={mapImage?.width}
									height={mapImage?.height}
								>
									{({ ref, open }) => (
										<Button
											ref={ref as Ref<HTMLButtonElement>}
											variant='outlined'
											color='primary'
											onClick={(e) => mapImage && open(e)}
											disabled={!mapImage}
											fullWidth
											size='small'
										>
											{t('pages.alerts.itemDetails.showMapPreview')}
										</Button>
									)}
								</Item>
							</Gallery>
						),
					},
				]
			: []),
	];

	const stretchLastButton = actionButtons.length % 2 === 1;

	const content = (
		<>
			<Card
				sx={{
					overflow: 'auto',
					overscrollBehavior: 'contain',
					minHeight: 0,
					...(isDesktop ? { boxShadow: 3, borderRadius: 2 } : { boxShadow: 'none', borderRadius: 0 }),
				}}
			>
				<Box
					sx={{
						position: 'sticky',
						top: -1,
						zIndex: 2,
						backgroundColor: 'background.paper',
						px: 2,
						pt: isDesktop ? 2 : 1.5,
						pb: 1.5,
						borderBottom: 1,
						borderColor: 'borders.primary',
					}}
				>
					{!isDesktop && (
						<Box sx={{ display: 'flex', justifyContent: 'center', pb: 1.5 }}>
							<Box sx={{ width: 40, height: 4, borderRadius: 2, backgroundColor: 'borders.primary' }} />
						</Box>
					)}
					<Box sx={{ pr: 4 }}>
						<Stack direction='row' alignItems='center' spacing={1}>
							{getItemIcon()}
							<Typography variant='h4' sx={{ fontWeight: 'bold' }}>
								{getItemType()}: {lockerGateItem.userFriendlyId}
							</Typography>
						</Stack>
						{lockerGateItem.lockerDetails && (
							<Typography variant='h5' mt={1}>
								{getLockerDimensions(lockerGateItem.lockerDetails.lockerDetailsInfo)}
							</Typography>
						)}
					</Box>
					<IconButton
						onClick={onClose}
						sx={{ position: 'absolute', right: 16, top: isDesktop ? 16 : 28, padding: 0 }}
					>
						<CloseIcon />
					</IconButton>
				</Box>

				<CardContent sx={{ pt: 2 }}>
					<Box
						sx={{
							border: 1,
							borderColor: 'borders.primary',
							borderRadius: 2,
							overflow: 'hidden',
						}}
					>
						{(lockerGateItem.hasElectricLock || !lockerGateItem.isLocker) && (
							<>
								{/* An unmonitored box reports a synthesized status — hide it, but keep the
								    last-opened date below, which is history rather than current state. */}
								{lockerGateItem.isMonitored && (
									<FieldRow
										label={t('common.openStatus')}
										value={t(`enums.openStatus.${OpenStatus[lockerGateItem.openStatus]}`, {
											context: isGate ? 'gate' : undefined,
										})}
									/>
								)}

								<FieldRow
									label={t('common.lastOpened')}
									value={
										lockerGateItem.lastOpenedAtUTC
											? formatDateTimeWithSeconds(lockerGateItem.lastOpenedAtUTC)
											: '-'
									}
								/>

								{lockerGateItem.lastUserName && (
									<FieldRow label={t('common.lastOpenedBy')} value={lockerGateItem.lastUserName} />
								)}
							</>
						)}
						{lockerGateItem.lockerDetails?.outOfUseReason && (
							<FieldRow
								label={t('common.outOfUseReason')}
								value={lockerGateItem.lockerDetails.outOfUseReason}
							/>
						)}
						{!rentalDetails && catalogPriceRows}
						{rentalDetails && (
							<>
								<FieldRow
									label={t('common.rentalStatus')}
									value={t(`enums.orderStatus.${DictOrderStatus[rentalDetails.orderStatus]}`)}
									valueColor={getRentStatusColor(rentalDetails, theme)}
								/>

								<FieldRow label={t('common.rentalPeriod')} value={getRentalPeriod()} />
								{getRentalPrice() && (
									<FieldRow
										label={priceLabel}
										value={
											<>
												{getRentalPrice()}
												{catalogDiffersFromRental() && (
													<Typography
														variant='caption'
														color='text.secondary'
														fontWeight={400}
														display='block'
													>
														{t('common.price')}: {getCatalogPrice()}
													</Typography>
												)}
											</>
										}
									/>
								)}
								<FieldRow
									label={t('common.orderPaymentState')}
									value={getOrderPaymentState(rentalDetails, t)}
									valueColor={
										rentalDetails.isPaid || rentalDetails.isPaid === null
											? undefined
											: 'error.light'
									}
								/>
								{tenantDetails && (
									<>
										<FieldRow
											label={t('common.tenant')}
											value={`${tenantDetails.firstName} ${tenantDetails.lastName}`}
										/>

										<ContactItem
											label={t('common.inputs.phone')}
											href={`tel:${tenantDetails.phone?.replace(/\s/g, '') || ''}`}
										>
											{formatPhoneNumber(tenantDetails.phone, tenantDetails.phoneCountryCode)}
										</ContactItem>

										<ContactItem
											label={t('common.inputs.email')}
											href={`mailto:${tenantDetails.email || ''}`}
										>
											{tenantDetails.email}
										</ContactItem>
									</>
								)}
							</>
						)}
						{accessCode && (
							<CodeRow
								label={accessCodeLabel}
								code={accessCode}
								suffix={accessCodeSuffix}
								editLabel={t('pages.alerts.itemDetails.editAccessCode')}
								onEdit={() => setEditingCode('access')}
							/>
						)}
						{emergencyCode && (
							<CodeRow
								label={emergencyCodeLabel}
								code={emergencyCode}
								suffix={accessCodeSuffix}
								labelAdornment={emergencyCodeInfo}
								editLabel={t('pages.alerts.itemDetails.editEmergencyAccessCode')}
								onEdit={() => setEditingCode('emergency')}
							/>
						)}
					</Box>

					{actionButtons.length > 0 && (
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: 'repeat(2, 1fr)',
								gap: 1,
								width: '100%',
								mt: 2,
							}}
						>
							{actionButtons.map((button, index) => (
								<Box
									key={button.key}
									sx={{
										display: 'flex',
										gridColumn:
											stretchLastButton && index === actionButtons.length - 1
												? 'span 2'
												: undefined,
									}}
								>
									{button.element()}
								</Box>
							))}
						</Box>
					)}

					{editingCode && (
						<EditCodeDialog
							open
							onClose={() => setEditingCode(null)}
							lockerId={lockerGateItem.id}
							userFriendlyId={lockerGateItem.userFriendlyId}
							typeLabel={getItemType()}
							codeKind={editingCode}
							codeLabel={editingCode === 'emergency' ? emergencyCodeLabel : accessCodeLabel}
							currentCode={(editingCode === 'emergency' ? emergencyCode : accessCode) ?? ''}
						/>
					)}

					{/* Nothing can write a history for this box, so the events endpoint would answer with an
					    error - asking anyway meant the section appeared as a loader and then vanished,
					    resizing the popup twice. Undefined means an API that predates the flag: ask, as before. */}
					{open && lockerGateItem.hasEventSource !== false && (
						<ItemEvents
							itemId={lockerGateItem.id}
							isLocker={lockerGateItem.isLocker}
							locationId={locationStore.selectedLocation?.id ?? ''}
							showCameraPreview={showCameraPreview}
						/>
					)}
				</CardContent>
			</Card>
		</>
	);

	if (isDesktop) {
		return (
			<Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
				{content}
			</Dialog>
		);
	}

	return (
		<SwipeableDrawer
			anchor='bottom'
			open={open}
			onClose={onClose}
			onOpen={() => undefined}
			disableSwipeToOpen
			ModalProps={{ keepMounted: false }}
			slotProps={{
				paper: {
					sx: {
						borderTopLeftRadius: 16,
						borderTopRightRadius: 16,
						maxHeight: '92vh',
					},
				},
			}}
		>
			{content}
		</SwipeableDrawer>
	);
};

export const ItemDetailsDialog = observer(ItemDetailsDialogComponent);
